import React, { useState } from "react";
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Button,
  Flex,
  Spacer,
  Badge,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import ResponsivePagination from "react-responsive-pagination";
import {
  MdSearch,
  MdFilterList,
  MdVisibility,
  MdAdd,
  MdRefresh,
  MdMoreVert,
  MdEdit,
  MdDelete,
} from "react-icons/md";
import {
  useUsers,
  useUserOperations,
  useUserStatistics,
} from "../hooks/useUsers";
import { useTaskOperations, useTaskStats } from "../hooks/useTasks";
import { handleApiError, handleApiSuccess } from "../utils/apiHelpers";
import ConfirmationDialog from "../components/common/ConfirmationDialog";

const UserRow = ({
  user,
  isSelected,
  onToggle,
  onAssignTask,
  onViewProfile,
  onDeleteUser,
  onEditUser,
}) => {
  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "red";
      case "annotator":
        return "blue";
      case "reviewer":
        return "purple";
      default:
        return "gray";
    }
  };

  const formatValue = (value) => {
    return value || value === 0 ? value : "-";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  return (
    <Tr bg="#121212" borderColor="#3A3A3A" _hover={{ bg: "#1A1A1A" }}>
      <Td borderColor="#3A3A3A" py="4">
        <Checkbox
          isChecked={isSelected}
          onChange={onToggle}
          colorScheme="orange"
        />
      </Td>
      <Td borderColor="#3A3A3A" color="white" fontWeight="medium">
        {user.username || "Unknown User"}
      </Td>
      <Td borderColor="#3A3A3A" color="gray.300" textAlign="center">
        <Badge
          colorScheme={getRoleColor(user.role)}
          variant="solid"
          borderRadius="md"
        >
          {user.role?.charAt(0).toUpperCase() + user.role?.slice(1) ||
            "Unknown"}
        </Badge>
      </Td>
      <Td borderColor="#3A3A3A" color="gray.300" textAlign="center">
        {user.total_done_ids
          ? `Completed - ${user.total_done_ids}/${user.total_assigned_ids}`
          : "-"}
      </Td>
      <Td borderColor="#3A3A3A" color="gray.300" textAlign="center">
        {user.total_tasks_completed
          ? `${user.total_tasks_completed} tasks`
          : "No tasks"}
      </Td>
      <Td borderColor="#3A3A3A" color="gray.300" textAlign="center">
        {formatDate(user.created_at)}
      </Td>
      <Td borderColor="#3A3A3A">
        <HStack spacing="2">
          <Button
            size="xs"
            variant="outline"
            borderColor="#FF9900"
            color="#FF9900"
            _hover={{ bg: "#FF9900", color: "black" }}
            onClick={() => onViewProfile(user)}
          >
            View Profile
          </Button>
          <Button
            size="xs"
            variant="outline"
            borderColor="#FF9900"
            color="#FF9900"
            _hover={{ bg: "#FF9900", color: "black" }}
            onClick={() => onAssignTask(user)}
          >
            Assign Task
          </Button>
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="More actions"
              icon={<MdMoreVert />}
              size="xs"
              variant="ghost"
              colorScheme="gray"
              color="gray.400"
              _hover={{ color: "#FF9900", bg: "gray.700" }}
            />
            <MenuList bg="#1C1C1C" borderColor="#3A3A3A" borderWidth="1px">
              <MenuItem
                icon={<MdEdit />}
                bg="transparent"
                color="blue.400"
                _hover={{ bg: "#2A2A2A" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditUser(user);
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                icon={<MdDelete />}
                bg="transparent"
                color="red.400"
                _hover={{ bg: "#2A2A2A" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteUser(user);
                }}
              >
                Delete
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Td>
    </Tr>
  );
};

const AddUserModal = ({ isOpen, onClose, onUserCreated, editUser = null }) => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    role: "",
  });

  const { createUser, updateUser, loading } = useUserOperations();

  const isEditMode = !!editUser;

  // Set form data when editing a user
  React.useEffect(() => {
    if (editUser) {
      setFormData({
        username: editUser.username || "",
        password: "", // Don't prefill password for security
        role: editUser.role || "",
      });
    } else {
      setFormData({
        username: "",
        password: "",
        role: "",
      });
    }
  }, [editUser]);

  // Reset form when modal closes
  const handleClose = () => {
    setFormData({
      username: "",
      password: "",
      role: "",
    });
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddUser = async () => {
    if (!formData.username || !formData.role) {
      handleApiError(new Error("Please fill in all required fields"));
      return;
    }

    // For edit mode, password is optional
    if (!isEditMode && !formData.password) {
      handleApiError(new Error("Password is required for new users"));
      return;
    }

    try {
      if (isEditMode) {
        const updateData = {
          username: formData.username,
          role: formData.role,
        };
        // Only include password if it's provided
        if (formData.password) {
          updateData.password = formData.password;
        }

        await updateUser(editUser.id, updateData);
        handleApiSuccess("User updated successfully!");
      } else {
        await createUser(formData);
        handleApiSuccess("User created successfully!");
      }

      onUserCreated?.();

      // Reset form and close modal
      setFormData({
        username: "",
        password: "",
        role: "",
      });
      onClose();
    } catch (error) {
      handleApiError(
        error,
        isEditMode ? "Failed to update user" : "Failed to create user"
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay
        bg="blackAlpha.800"
        backdropFilter="blur(10px)"
        WebkitBackdropFilter="blur(10px)"
      />
      <ModalContent bg="#1C1C1C" borderColor="#3A3A3A" borderWidth="1px">
        <ModalHeader
          color="white"
          borderBottom="1px"
          borderBottomColor="#3A3A3A"
        >
          {isEditMode ? "Edit User" : "Add New User"}
          <Text fontSize="sm" color="gray.400" fontWeight="normal" mt="1">
            {isEditMode
              ? "Update user account information"
              : "Create a new user account for your platform"}
          </Text>
        </ModalHeader>
        <ModalCloseButton color="gray.400" />

        <ModalBody py="6">
          <VStack spacing="4" align="stretch">
            {/* Username */}
            <FormControl isRequired>
              <FormLabel color="gray.300" fontSize="sm">
                Username
              </FormLabel>
              <Input
                placeholder="Enter Username"
                value={formData.username}
                onChange={(e) => handleInputChange("username", e.target.value)}
                bg="#121212"
                borderColor="#3A3A3A"
                color="white"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "#FF9900" }}
                _focus={{
                  borderColor: "#FF9900",
                  boxShadow: "0 0 0 1px #FF9900",
                }}
              />
            </FormControl>

            {/* Password */}
            <FormControl isRequired={!isEditMode}>
              <FormLabel color="gray.300" fontSize="sm">
                Password{" "}
                {isEditMode && (
                  <Text as="span" color="gray.500">
                    (leave blank to keep current)
                  </Text>
                )}
              </FormLabel>
              <Input
                placeholder={
                  isEditMode
                    ? "Enter new password (optional)"
                    : "Enter Password"
                }
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                bg="#121212"
                borderColor="#3A3A3A"
                color="white"
                _placeholder={{ color: "gray.500" }}
                _hover={{ borderColor: "#FF9900" }}
                _focus={{
                  borderColor: "#FF9900",
                  boxShadow: "0 0 0 1px #FF9900",
                }}
              />
            </FormControl>

            {/* Role */}
            <FormControl isRequired>
              <FormLabel color="gray.300" fontSize="sm">
                Role
              </FormLabel>
              <Select
                placeholder="Select Role"
                value={formData.role}
                onChange={(e) => handleInputChange("role", e.target.value)}
                bg="#121212"
                borderColor="#3A3A3A"
                color="white"
                _hover={{ borderColor: "#FF9900" }}
                _focus={{
                  borderColor: "#FF9900",
                  boxShadow: "0 0 0 1px #FF9900",
                }}
              >
                <option value="Admin" style={{ backgroundColor: "#121212" }}>
                  Admin
                </option>
                <option
                  value="Annotator"
                  style={{ backgroundColor: "#121212" }}
                >
                  Annotator
                </option>
                <option value="Reviewer" style={{ backgroundColor: "#121212" }}>
                  Reviewer
                </option>
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px" borderTopColor="#3A3A3A">
          <Button
            variant="outline"
            mr={3}
            onClick={handleClose}
            borderColor="#3A3A3A"
            color="gray.300"
            _hover={{ bg: "gray.700" }}
            isDisabled={loading}
          >
            Cancel
          </Button>
          <Button
            bg="#FF9900"
            color="black"
            _hover={{ bg: "#E68900" }}
            onClick={handleAddUser}
            isLoading={loading}
            loadingText={isEditMode ? "Updating..." : "Creating..."}
            isDisabled={
              !formData.username ||
              !formData.role ||
              (!isEditMode && !formData.password)
            }
          >
            {isEditMode ? "Update User" : "Add User"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const AssignTaskModal = ({ isOpen, onClose, selectedUser, onTaskAssigned }) => {
  console.log(selectedUser);

  const [searchTask, setSearchTask] = useState("");
  const [selectedTask, setSelectedTask] = useState("");

  const { tasks: availableTasks, loading: tasksLoading } = useTaskStats();
  const { assignTask, loading: assigning } = useTaskOperations();

  // Reset form when modal closes
  const handleClose = () => {
    setSearchTask("");
    setSelectedTask("");
    onClose();
  };

  // Filter unassigned tasks
  const unassignedTasks = availableTasks.filter(
    (task) => !task.annotated_by && !task.reviewed_by
  );

  const handleAssignTask = async () => {
    if (!selectedTask || !selectedUser) {
      handleApiError(new Error("Please select a task to assign"));
      return;
    }

    try {
      console.log("Assigning task:", selectedTask, "to user:", selectedUser);

      await assignTask({
        username: selectedUser.username,
        task_id: selectedTask,
        user_role: selectedUser.role,
      });

      handleApiSuccess(
        `Task assigned to ${selectedUser.username} successfully!`
      );
      onTaskAssigned?.();

      // Reset form and close modal
      setSearchTask("");
      setSelectedTask("");
      onClose();
    } catch (error) {
      handleApiError(error, "Failed to assign task");
    }
  };

  const filteredTasks = unassignedTasks.filter((task) =>
    task.task_id?.toLowerCase().includes(searchTask.toLowerCase())
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay
        bg="blackAlpha.800"
        backdropFilter="blur(10px)"
        WebkitBackdropFilter="blur(10px)"
      />
      <ModalContent bg="#1C1C1C" borderColor="#3A3A3A" borderWidth="1px">
        <ModalHeader
          color="white"
          borderBottom="1px"
          borderBottomColor="#3A3A3A"
        >
          Assign Task
          <Text fontSize="sm" color="gray.400" fontWeight="normal" mt="1">
            Allocate a task to a team member for completion
          </Text>
        </ModalHeader>
        <ModalCloseButton color="gray.400" />

        <ModalBody py="6">
          <VStack spacing="4" align="stretch">
            {/* Username Display */}
            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                Username
              </FormLabel>
              <Input
                value={selectedUser?.username || "Username"}
                isReadOnly
                bg="#121212"
                borderColor="#3A3A3A"
                color="white"
                _focus={{ borderColor: "#3A3A3A" }}
              />
            </FormControl>

            {/* Select Task */}
            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                Select Task
              </FormLabel>
              <InputGroup mb="4">
                <InputLeftElement pointerEvents="none">
                  <MdSearch color="#666" />
                </InputLeftElement>
                <Input
                  placeholder="Search Task"
                  value={searchTask}
                  onChange={(e) => setSearchTask(e.target.value)}
                  bg="#121212"
                  borderColor="#3A3A3A"
                  color="white"
                  _placeholder={{ color: "gray.500" }}
                  _hover={{ borderColor: "#FF9900" }}
                  _focus={{
                    borderColor: "#FF9900",
                    boxShadow: "0 0 0 1px #FF9900",
                  }}
                />
              </InputGroup>

              {/* Task List */}
              {tasksLoading ? (
                <Flex justify="center" py={4}>
                  <Spinner color="orange.400" />
                </Flex>
              ) : (
                <VStack
                  spacing="3"
                  align="stretch"
                  maxH="200px"
                  overflowY="auto"
                >
                  {filteredTasks.length === 0 ? (
                    <Text color="gray.400" textAlign="center" py={4}>
                      {unassignedTasks.length === 0
                        ? "No unassigned tasks available"
                        : "No tasks match your search"}
                    </Text>
                  ) : (
                    filteredTasks.map((task) => (
                      <HStack
                        key={task.task_id}
                        p="3"
                        bg={
                          selectedTask === task.task_id ? "#2D3748" : "#121212"
                        }
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={
                          selectedTask === task.task_id ? "#FF9900" : "#3A3A3A"
                        }
                        justify="space-between"
                        cursor="pointer"
                        onClick={() => setSelectedTask(task.task_id)}
                        _hover={{
                          bg:
                            selectedTask === task.task_id
                              ? "#2D3748"
                              : "#2A2A2A",
                        }}
                      >
                        <VStack align="start" spacing="0">
                          <Text color="white" fontSize="sm" fontWeight="medium">
                            {task.task_id}
                          </Text>
                          <Text color="gray.400" fontSize="xs">
                            Batch: {task.batch_id || "N/A"} | Models:{" "}
                            {task.total_models || 0}
                          </Text>
                        </VStack>
                        <VStack align="end" spacing="0">
                          <Text color="gray.300" fontSize="xs">
                            Status: {task.status}
                          </Text>
                          {selectedTask === task.task_id && (
                            <Text color="orange.400" fontSize="xs">
                              Selected
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                    ))
                  )}
                </VStack>
              )}
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px" borderTopColor="#3A3A3A">
          <Button
            variant="outline"
            mr={3}
            onClick={handleClose}
            borderColor="#3A3A3A"
            color="gray.300"
            _hover={{ bg: "gray.700" }}
            isDisabled={assigning}
          >
            Cancel
          </Button>
          <Button
            bg="#FF9900"
            color="black"
            _hover={{ bg: "#E68900" }}
            onClick={handleAssignTask}
            isDisabled={!selectedTask || assigning}
            isLoading={assigning}
            loadingText="Assigning..."
          >
            Assign Task
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ViewProfileModal = ({ isOpen, onClose, selectedUser }) => {
  if (!selectedUser) return null;

  // Reset when modal closes (clear any internal state if needed)
  const handleClose = () => {
    onClose();
  };

  // For now, use sample data since useUserTaskHistory is not implemented yet
  // TODO: Implement useUserTaskHistory hook when user task history API is available
  const taskHistory = [
    {
      id: 1,
      task_id: "Task_001",
      role: "Classification",
      objects_count: 2,
      total_models: 3000,
      method: "Remote - Albacore",
      status: "in_progress",
    },
    {
      id: 2,
      task_id: "Task_002",
      role: "Classification",
      objects_count: 2,
      total_models: 3000,
      method: "Remote - Albacore",
      status: "completed",
    },
    {
      id: 3,
      task_id: "Task_003",
      role: "Classification",
      objects_count: 2,
      total_models: 3000,
      method: "Remote - Albacore",
      status: "in_progress",
    },
  ];
  const historyLoading = false;

  // Calculate statistics from real data
  const totalTasks = selectedUser.total_assigned_ids || 0;
  const completedTasks = taskHistory.filter(
    (task) => task.status === "completed"
  ).length;
  const inProgressTasks = taskHistory.filter(
    (task) => task.status === "in_progress"
  ).length;

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "in_progress":
      case "in progress":
        return "orange";
      case "completed":
        return "green";
      case "not_started":
      case "not started":
        return "gray";
      default:
        return "gray";
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl" isCentered>
      <ModalOverlay
        bg="blackAlpha.800"
        backdropFilter="blur(10px)"
        WebkitBackdropFilter="blur(10px)"
      />
      <ModalContent
        bg="#1C1C1C"
        borderColor="#3A3A3A"
        borderWidth="1px"
        maxW="90vw"
        mx="auto"
      >
        <ModalHeader
          color="white"
          borderBottom="1px"
          borderBottomColor="#3A3A3A"
        >
          <HStack justify="space-between" align="center">
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                {selectedUser.username}
              </Text>
            </Box>
            <Badge
              colorScheme="orange"
              variant="solid"
              borderRadius="md"
              px="3"
              py="1"
            >
              {selectedUser.role}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="gray.400" />

        <ModalBody py="6">
          <VStack spacing="6" align="stretch">
            {/* Header Info */}
            <Box>
              <Text color="white" fontSize="lg" fontWeight="semibold" mb="2">
                Task Completion History
              </Text>
              <Text color="gray.400" fontSize="sm">
                Track user progress and task completion details
              </Text>
            </Box>

            {/* Completion History Table */}
            <Box
              bg="#121212"
              borderRadius="lg"
              borderColor="#3A3A3A"
              borderWidth="1px"
              overflow="hidden"
            >
              <Table variant="simple" size="md">
                <Thead bg="#1A1A1A">
                  <Tr>
                    <Th
                      borderColor="#3A3A3A"
                      color="gray.300"
                      fontSize="xs"
                      fontWeight="semibold"
                    >
                      TASK NAME
                    </Th>
                    <Th
                      borderColor="#3A3A3A"
                      color="gray.300"
                      fontSize="xs"
                      fontWeight="semibold"
                      textAlign="center"
                    >
                      ROLE
                    </Th>
                    <Th
                      borderColor="#3A3A3A"
                      color="gray.300"
                      fontSize="xs"
                      fontWeight="semibold"
                      textAlign="center"
                    >
                      NO OF OBJECTS
                    </Th>
                    <Th
                      borderColor="#3A3A3A"
                      color="gray.300"
                      fontSize="xs"
                      fontWeight="semibold"
                      textAlign="center"
                    >
                      TOTAL MODELS
                    </Th>
                    <Th
                      borderColor="#3A3A3A"
                      color="gray.300"
                      fontSize="xs"
                      fontWeight="semibold"
                      textAlign="center"
                    >
                      METHOD
                    </Th>
                    <Th
                      borderColor="#3A3A3A"
                      color="gray.300"
                      fontSize="xs"
                      fontWeight="semibold"
                      textAlign="center"
                    >
                      STATUS
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {historyLoading ? (
                    <Tr>
                      <Td colSpan={6} textAlign="center" py={8}>
                        <Spinner color="orange.400" />
                      </Td>
                    </Tr>
                  ) : taskHistory.length === 0 ? (
                    <Tr>
                      <Td colSpan={6} textAlign="center" py={8}>
                        <Text color="gray.400">No task history available</Text>
                      </Td>
                    </Tr>
                  ) : (
                    taskHistory.map((task) => (
                      <Tr
                        key={task.id}
                        bg="#121212"
                        borderColor="#3A3A3A"
                        _hover={{ bg: "#1A1A1A" }}
                      >
                        <Td
                          borderColor="#3A3A3A"
                          color="white"
                          fontWeight="medium"
                          py="4"
                        >
                          {task.task_id}
                        </Td>
                        <Td
                          borderColor="#3A3A3A"
                          color="gray.300"
                          textAlign="center"
                        >
                          <Badge
                            colorScheme="blue"
                            variant="solid"
                            borderRadius="md"
                          >
                            {task.role}
                          </Badge>
                        </Td>
                        <Td
                          borderColor="#3A3A3A"
                          color="gray.300"
                          textAlign="center"
                        >
                          {task.objects_count || 0}
                        </Td>
                        <Td
                          borderColor="#3A3A3A"
                          color="gray.300"
                          textAlign="center"
                        >
                          {task.total_models}
                        </Td>
                        <Td
                          borderColor="#3A3A3A"
                          color="gray.300"
                          textAlign="center"
                        >
                          {task.method}
                        </Td>
                        <Td borderColor="#3A3A3A" textAlign="center">
                          <Badge
                            colorScheme={getStatusColor(task.status)}
                            variant="solid"
                            borderRadius="md"
                          >
                            {task.status
                              .replace("_", " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </Badge>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px" borderTopColor="#3A3A3A">
          <Button
            variant="outline"
            onClick={handleClose}
            borderColor="#3A3A3A"
            color="gray.300"
            _hover={{ bg: "gray.700" }}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const Users = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedUserForTask, setSelectedUserForTask] = useState(null);
  const [selectedUserForProfile, setSelectedUserForProfile] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isAssignTaskOpen,
    onOpen: onAssignTaskOpen,
    onClose: onAssignTaskClose,
  } = useDisclosure();
  const {
    isOpen: isViewProfileOpen,
    onOpen: onViewProfileOpen,
    onClose: onViewProfileClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: onDeleteConfirmOpen,
    onClose: onDeleteConfirmClose,
  } = useDisclosure();

  // Use real API data with pagination
  const {
    users,
    pagination,
    loading: usersLoading,
    refetch: refetchUsers,
  } = useUsers(currentPage, perPage);
  const { statistics, loading: statsLoading } = useUserStatistics();
  const { deleteUser, loading: deletingUser } = useUserOperations();

  const handleAssignTask = (user) => {
    setSelectedUserForTask(user);
    onAssignTaskOpen();
  };

  const handleViewProfile = (user) => {
    setSelectedUserForProfile(user);
    onViewProfileOpen();
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    onDeleteConfirmOpen();
  };

  const handleEditUser = (user) => {
    setUserToEdit(user);
    onOpen(); // Reuse the same modal for editing
  };

  const handleModalClose = () => {
    setUserToEdit(null); // Clear edit user when modal closes
    onClose();
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    try {
      const response = await deleteUser(userToDelete.id);

      // Handle success response from backend
      if (response && response.message) {
        handleApiSuccess(response.message);
      } else {
        handleApiSuccess(
          `User "${userToDelete.username}" deleted successfully!`
        );
      }

      // If we deleted the last user on the current page and we're not on page 1,
      // go back to the previous page
      if (users.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        refetchUsers();
      }
      onDeleteConfirmClose();
      setUserToDelete(null);
    } catch (error) {
      // Handle specific error responses from backend
      let errorMessage = "Failed to delete user";

      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (errorData.error) {
          errorMessage = errorData.error;

          // If there are assigned task IDs, include them in the message
          if (errorData.task_ids && errorData.task_ids.length > 0) {
            errorMessage += `. Assigned tasks: ${errorData.task_ids.join(
              ", "
            )}`;
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      handleApiError(new Error(errorMessage));
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedUsers(new Set()); // Clear selected users when changing pages
  };

  const handleUserCreated = () => {
    // Stay on current page after creating user
    refetchUsers();
  };

  const handleTaskAssigned = () => {
    // Stay on current page after task assignment
    refetchUsers();
  };

  const handleSelectUser = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map((user) => user.id)));
    }
  };

  // Since pagination is handled by the API, we use users directly
  // TODO: Implement search and filter through API parameters
  const filteredUsers = users;

  // Calculate total pages - use API data if available, otherwise calculate based on user count
  const totalPages =
    pagination?.pages ||
    Math.ceil((pagination?.total || users.length) / perPage);
  const totalItems = pagination?.total || users.length;

  return (
    <Box>
      {/* Header */}
      <Box mb="6">
        <Flex align="center" justify="space-between">
          <Box w="full" align="center">
            <Text fontSize="3xl" fontWeight="bold" color="white" mb="2">
              User Management
            </Text>
            <Text color="gray.400" fontSize="md">
              Manage user accounts and permissions
            </Text>
          </Box>
          <Button
            leftIcon={<MdAdd />}
            bg="#FF9900"
            color="black"
            _hover={{ bg: "#E68900" }}
            onClick={onOpen}
          >
            Add User
          </Button>
        </Flex>
      </Box>

      {/* Statistics Cards */}
      {/* {!statsLoading && statistics && (
          <Box mb="6">
            <HStack spacing="6" justify="space-around">
              <Box
                bg="#121212"
                p="6"
                borderRadius="lg"
                borderColor="#3A3A3A"
                borderWidth="1px"
                textAlign="center"
                minW="200px"
              >
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {statistics.total_users || users.length}
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Total Users
                </Text>
              </Box>
              <Box
                bg="#121212"
                p="6"
                borderRadius="lg"
                borderColor="#3A3A3A"
                borderWidth="1px"
                textAlign="center"
                minW="200px"
              >
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {statistics.active_users ||
                    users.filter((u) => u.total_assigned_ids > 0).length}
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Active Users
                </Text>
              </Box>
              <Box
                bg="#121212"
                p="6"
                borderRadius="lg"
                borderColor="#3A3A3A"
                borderWidth="1px"
                textAlign="center"
                minW="200px"
              >
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {statistics.total_tasks_assigned ||
                    users.reduce(
                      (sum, user) => sum + (user.total_assigned_ids || 0),
                      0
                    )}
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Total Tasks Assigned
                </Text>
              </Box>
            </HStack>
          </Box>
        )} */}

      {/* Search and Filter Bar */}
      <Flex mb="6" gap="4" align="center">
        <InputGroup maxW="300px">
          <InputLeftElement pointerEvents="none">
            <MdSearch color="#666" />
          </InputLeftElement>
          <Input
            placeholder="Search User"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            bg="#121212"
            borderColor="#3A3A3A"
            color="white"
            _placeholder={{ color: "gray.500" }}
            _hover={{ borderColor: "#FF9900" }}
            _focus={{
              borderColor: "#FF9900",
              boxShadow: "0 0 0 1px #FF9900",
            }}
          />
        </InputGroup>

        <Select
          placeholder="Select Role"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          maxW="200px"
          bg="#121212"
          borderColor="#3A3A3A"
          color="white"
          _hover={{ borderColor: "#FF9900" }}
          _focus={{ borderColor: "#FF9900", boxShadow: "0 0 0 1px #FF9900" }}
        >
          <option value="Admin" style={{ backgroundColor: "#121212" }}>
            Admin
          </option>
          <option value="Annotator" style={{ backgroundColor: "#121212" }}>
            Annotator
          </option>
          <option value="Reviewer" style={{ backgroundColor: "#121212" }}>
            Reviewer
          </option>
        </Select>

        <Spacer />

        <Button
          leftIcon={<MdFilterList />}
          variant="outline"
          borderColor="#3A3A3A"
          color="gray.300"
          _hover={{ bg: "#FF9900", color: "black", borderColor: "#FF9900" }}
        >
          Filter
        </Button>
      </Flex>

      {/* Users Table */}
      {usersLoading ? (
        <Box
          bg="#121212"
          borderRadius="lg"
          borderColor="#3A3A3A"
          borderWidth="1px"
          p="8"
          textAlign="center"
        >
          <Spinner color="orange.400" size="lg" />
          <Text color="gray.400" mt="4">
            Loading users...
          </Text>
        </Box>
      ) : users.length === 0 ? (
        <Box
          bg="#121212"
          borderRadius="lg"
          borderColor="#3A3A3A"
          borderWidth="1px"
          p="8"
          textAlign="center"
        >
          <Text color="gray.400" fontSize="lg">
            No users found
          </Text>
          <Text color="gray.500" fontSize="sm" mt="2">
            Create your first user account to get started
          </Text>
        </Box>
      ) : (
        <Box
          bg="#121212"
          borderRadius="lg"
          borderColor="#3A3A3A"
          borderWidth="1px"
          overflow="hidden"
        >
          <Table variant="simple" size="md">
            <Thead bg="#1A1A1A">
              <Tr>
                <Th borderColor="#3A3A3A" py="4">
                  <Checkbox
                    isChecked={
                      selectedUsers.size === filteredUsers.length &&
                      filteredUsers.length > 0
                    }
                    isIndeterminate={
                      selectedUsers.size > 0 &&
                      selectedUsers.size < filteredUsers.length
                    }
                    onChange={handleSelectAll}
                    colorScheme="orange"
                  />
                </Th>
                <Th
                  borderColor="#3A3A3A"
                  color="gray.300"
                  fontSize="xs"
                  fontWeight="semibold"
                >
                  USER NAME
                </Th>
                <Th
                  borderColor="#3A3A3A"
                  color="gray.300"
                  fontSize="xs"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  ROLE
                </Th>
                <Th
                  borderColor="#3A3A3A"
                  color="gray.300"
                  fontSize="xs"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  RESPONSIBILITIES
                </Th>
                <Th
                  borderColor="#3A3A3A"
                  color="gray.300"
                  fontSize="xs"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  CURRENT TASK
                </Th>
                <Th
                  borderColor="#3A3A3A"
                  color="gray.300"
                  fontSize="xs"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  WORK YEAR COMPLETED
                </Th>
                <Th
                  borderColor="#3A3A3A"
                  color="gray.300"
                  fontSize="xs"
                  fontWeight="semibold"
                  textAlign="center"
                >
                  ACTIONS
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredUsers.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isSelected={selectedUsers.has(user.id)}
                  onToggle={() => handleSelectUser(user.id)}
                  onAssignTask={handleAssignTask}
                  onViewProfile={handleViewProfile}
                  onDeleteUser={handleDeleteUser}
                  onEditUser={handleEditUser}
                />
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination */}
      {!usersLoading && users.length > 0 && (
        <Box
          mt="6"
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap="4"
          bg="#121212"
          p="4"
          borderRadius="lg"
          borderColor="#3A3A3A"
          borderWidth="1px"
        >
          {/* Page Navigation Controls */}
          <Box display="flex" alignItems="center" gap="2">
            <Button
              size="sm"
              variant="outline"
              borderColor="#3A3A3A"
              color="gray.300"
              _hover={{ bg: "#FF9900", color: "black", borderColor: "#FF9900" }}
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
            >
              Previous
            </Button>

            {/* Page Numbers */}
            <Box display="flex" alignItems="center" gap="1">
              {totalPages > 1 ? (
                [...Array(Math.min(totalPages, 10))].map((_, index) => {
                  const page = index + 1;
                  return (
                    <Button
                      key={page}
                      size="sm"
                      variant={currentPage === page ? "solid" : "outline"}
                      bg={currentPage === page ? "#FF9900" : "transparent"}
                      color={currentPage === page ? "black" : "gray.300"}
                      borderColor="#3A3A3A"
                      _hover={{
                        bg: "#FF9900",
                        color: "black",
                        borderColor: "#FF9900",
                      }}
                      onClick={() => handlePageChange(page)}
                      minW="40px"
                    >
                      {page}
                    </Button>
                  );
                })
              ) : (
                <Button
                  size="sm"
                  variant="solid"
                  bg="#FF9900"
                  color="black"
                  borderColor="#FF9900"
                  minW="40px"
                >
                  1
                </Button>
              )}
            </Box>

            <Button
              size="sm"
              variant="outline"
              borderColor="#3A3A3A"
              color="gray.300"
              _hover={{ bg: "#FF9900", color: "black", borderColor: "#FF9900" }}
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage >= totalPages}
            >
              Next
            </Button>
          </Box>

          {/* Page Info */}
          <Box ml="4">
            <Text color="gray.400" fontSize="sm">
              Page {currentPage} of {totalPages}
            </Text>
          </Box>
        </Box>
      )}

      {/* Footer Info */}
      <Box
        mt="4"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        {selectedUsers.size > 0 && (
          <Text color="gray.400" fontSize="sm">
            {selectedUsers.size} user{selectedUsers.size !== 1 ? "s" : ""}{" "}
            selected
          </Text>
        )}
        {totalItems > 0 && (
          <Text color="gray.400" fontSize="sm">
            Showing {(currentPage - 1) * perPage + 1}-
            {Math.min(currentPage * perPage, totalItems)} of {totalItems} users
          </Text>
        )}
      </Box>

      {/* Add/Edit User Modal */}
      <AddUserModal
        isOpen={isOpen}
        onClose={handleModalClose}
        onUserCreated={handleUserCreated}
        editUser={userToEdit}
      />

      {/* Assign Task Modal */}
      <AssignTaskModal
        isOpen={isAssignTaskOpen}
        onClose={onAssignTaskClose}
        selectedUser={selectedUserForTask}
        onTaskAssigned={handleTaskAssigned}
      />

      {/* View Profile Modal */}
      <ViewProfileModal
        isOpen={isViewProfileOpen}
        onClose={onViewProfileClose}
        selectedUser={selectedUserForProfile}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isDeleteConfirmOpen}
        onClose={onDeleteConfirmClose}
        onConfirm={handleConfirmDelete}
        title="Delete User"
        message="Are you sure you want to delete this user? This will permanently remove their account and all associated data."
        confirmText="Delete User"
        cancelText="Cancel"
        confirmColorScheme="red"
        isLoading={deletingUser}
        loadingText="Deleting..."
        type="danger"
        itemName="User"
        itemIdentifier={userToDelete?.username}
      />
    </Box>
  );
};

export default Users;
